import React, { useState, useCallback } from 'react';
import {
  SubmitHandler,
  Controller,
  useFormContext,
} from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { useMutation, useLazyQuery } from '@apollo/client';

import { isValidPhoneNumber } from 'react-phone-number-input';

import { PreOnboardingParams, SetupForm } from '../PreOnboardingPage';

import { getPdtUid, getProductName, isSpark as isSparkHelper } from '@/utils/productHelpers';
import type { Product } from '@/utils/productHelpers';
import Button from '@/components/Button';
import { ENROLL_DIRECT_ACCESS_PATIENT_V2 } from '@/apollo/mutations';
import { EN } from '@/utils/constants';
import { getProgram } from '@/utils/posthog';
import { OverrideToggle } from '@/pages/Onboarding/components/OverrideToggle/OverrideToggle';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { useUserUid } from '@/hooks/useUserUid';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import { useUtmParams } from '@/hooks/useUtmParams';
import { parseSearchParams } from '@/utils/stringUtils';

interface SubmitEmailProps {
  onNext: () => void;
  onError: () => void;
}

const SubmitEmail = ({ onNext, onError }: SubmitEmailProps) => {
  const posthog = usePostHog();
  const { t, i18n } = useTranslation();
  const { userUid, setUserUid } = useUserUid();
  const { utmParams } = useUtmParams();
  const location = useLocation();
  const { product, slug } = useParams<PreOnboardingParams>();

  // Extract distinct_id from URL if present (from WebFlow)
  const searchParams = parseSearchParams(location.search);
  const urlDistinctId = searchParams.ph_distinct_id?.trim();
  const isSpark = isSparkHelper(product);
  const productForTitle = (isSpark ? 'spark' : 'sleepio') as Product;
  const [returnError, setReturnError] = useState(false);
  const [enrollDirectAccessPatientV2, { loading }] = useMutation(ENROLL_DIRECT_ACCESS_PATIENT_V2);

  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const {
    handleSubmit,
    control,
    formState,
  } = useFormContext<SetupForm>();

  const getPostHogDistinctId = useCallback(async (email: string): Promise<string> => {
    try {
      const getDistinctIdResult = await getDistinctId({
        variables: { email },
        onCompleted: (result) => {
          const distinctId = result?.posthogDistinctIdWithEmail?.distinctId;
          posthog?.identify(distinctId, { email });
        },
      });

      return getDistinctIdResult?.data?.posthogDistinctIdWithEmail?.distinctId;
    } catch (error) {
      console.error('Error fetching PostHog distinct ID:', error);
    }

    return '';
  }, [getDistinctId, posthog]);

  const onSubmit: SubmitHandler<SetupForm> = useCallback(async (data) => {
    if (returnError) {
      console.error('Intentional error produced by the override toggle');
      onError();

      return;
    }

    try {
      // Use distinct ID from URL if available, otherwise fetch from Cortex
      const posthogDistinctId = urlDistinctId || await getPostHogDistinctId(data.email);
      const response = await enrollDirectAccessPatientV2({
        variables: {
          email: data.email,
          language: i18n.language.toUpperCase().split('-')[0] || EN,
          organizationSlug: slug,
          pdtUid: getPdtUid(product),
          termsAndPrivacyAcknowledgedAt: new Date().toISOString(),
          posthogDistinctId,
          phone: data.phoneNumber || null,
          ...utmParams,
        },
      });

      const enrollDirectAccessPatientV2Result = response.data?.enrollDirectAccessPatientV2;
      if (enrollDirectAccessPatientV2Result.success) {
        const cortexId = enrollDirectAccessPatientV2Result.userUid || enrollDirectAccessPatientV2Result.patientUid;
        setUserUid(cortexId);
        posthog.capture('ActivationFlowSubmitted', {
          email: data.email,
          program: getProgram(product),
          'wellness.cortex_id': cortexId,
        });
        onNext();
      } else {
        console.warn('Enrollment failed:', response.errors);

        posthog.capture('ActivationFlowErrored', {
          email: data.email,
          program: getProgram(product),
        });
        onError();
      }
    } catch (e) {
      console.warn('Error during mutation:', e);
      onError();
    }
  }, [
    getPostHogDistinctId,
    userUid,
    setUserUid,
    enrollDirectAccessPatientV2,
    i18n,
    product,
    slug,
    posthog,
    onNext,
    onError,
    returnError,
    utmParams,
    urlDistinctId,
  ]);

  return (
    <>
      <Typography variant="h2" style={{ marginTop: 32 }} color="textPrimary">
        {t('coverage.pre_onboarding.welcome', { product: getProductName(productForTitle) })}
      </Typography>
      <Typography style={{ marginTop: 8, marginBottom: 32 }} color="textPrimary">
        {isSpark
          ? t('coverage.spark.person_enrolling.subtitle_enroll')
          : t('coverage.pre_onboarding.sleepio.subtitle')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="email"
          render={({ field }) => (
            <TextField
              id="email"
              data-testid="email-input"
              {...field}
              autoFocus
              variant="outlined"
              fullWidth
              required
              color="secondary"
              label={t('coverage.pre_onboarding.email.label')}
              placeholder={t('coverage.pre_onboarding.email.placeholder')}
              // forces label to always appear above input
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
        <div style={{ marginTop: 16 }} />
        {isSpark && (
          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: t('coverage.sleepio.patient.please_enter_valid_phone_number'),
              validate: (v) => isValidPhoneNumber(v || '')
                || t('coverage.sleepio.patient.please_enter_valid_phone_number'),
            }}
            render={({ field, fieldState }) => (
              <PhoneNumberInput
                id="phoneNumber"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('coverage.sleepio.patient.phone_label')}
                error={!!fieldState.error}
                helperText={
                  fieldState.error
                    ? t('coverage.sleepio.patient.please_enter_valid_phone_number')
                    : undefined
                }
                required
              />
            )}
          />
        )}
        <Controller
          name="termsAndPrivacy"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControlLabel
              style={{ marginTop: 24, marginBottom: 32, alignItems: 'flex-start' }}
              label={(
                <Typography>
                  {`${t('coverage.i_agree_to_the')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.terms_of_use.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {t('footer.terms_of_use.label')}
                  </Link>
                  {` ${t('coverage.and')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.privacy_notice.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {`${t('footer.privacy_notice.label')}.`}
                  </Link>
                </Typography>
              )}
              control={(
                <Checkbox {...field} />
              )}
            />
          )}
        />
        {isSpark && (
          <Controller
            name="agreeToReceiveTextMessages"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControlLabel
                style={{ marginTop: 24, marginBottom: 32 }}
                label={(
                  <Typography>
                    {t('coverage.spark.patient.agree_to_receive_text_messages_spark')}
                  </Typography>
                )}
                control={<Checkbox {...field} checked={!!field.value} />}
              />
            )}
          />
        )}
        <Button
          variant="contained"
          role="button"
          data-testid="submit-button"
          type="submit"
          fullWidth
          color="primary"
          disabled={!formState.isValid || loading}
          loading={loading}
          rounded
          style={
            isSpark
              ? {
                borderRadius: 100,
                ...(formState.isValid && !loading
                  ? { backgroundColor: '#077b65', color: '#FFFFFF' }
                  : {}),
              }
              : undefined
          }
        >
          {t('coverage.submit')}
        </Button>
      </form>
      {process.env.NODE_ENV !== 'production' && (
        <OverrideToggle
          userMessage="Simulate server error"
          override={setReturnError}
        />
      )}
    </>
  );
};

export default SubmitEmail;
